/**
 * Org chart (Оргструктура) — focus-mode дерево ПО ДОЛЖНОСТЯМ + подразделения.
 *
 *  • Tree view — «фокусный» режим поверх `/users/org-chart/holding`: первый уровень — юрлица
 *    холдинга, видимые смотрящему (`kind: 'company'`, с численностью), под каждым — ростер людей
 *    компании; узел = должность, человек — подпись на узле, совместитель выходит несколькими
 *    узлами. Фокус в URL (`/org?focus=<nodeId>`); старые ссылки с userId продолжают работать
 *    (фолбэк-поиск узла по держателю).
 *  • Departments view — `/users/departments/tree` (макет 29.10.2026): карточки юрлиц, в них
 *    подразделения 1С с руководителем (по признаку руководящей позиции); «+» раскрывает людей
 *    подразделения, клик по человеку — его карточка. Численность людей не показываем.
 *
 * Motion: keyed remount сцены (направленный slide по глубине), каскадные появления карточек
 * (`Enter`), растущие линии-коннекторы, «дышащее» кольцо вокруг фокуса, hover-lift. Всё
 * уважает reduce-motion через `prefersReducedMotion` / state-driven моушн.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQueryClient } from '@tanstack/react-query'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { api } from '../../src/api/client'
import { useApp, tr, type Lang } from '../../src/i18n'
import { useResponsive } from '../../src/lib/responsive'
import { pluralRu } from '../../src/lib/format'
import { useOrgChartHolding, useDepartmentTree } from '../../src/features/users/useUsers'
import { PersonDrawer, liveVM, type PersonVM } from '../../src/features/users/PersonDrawer'
import type { DepartmentTreeUnit, PositionNode } from '../../src/lib/types'
import { avColor } from '../../src/lib/avatar'
import { palette } from '../../src/theme/palette'
import { Avatar, Enter, FadeIn, Icon, prefersReducedMotion, QueryState, SectionCard, Tag } from '../../src/ui'
import { pressable } from '../../src/ui/primitives'
import { Page } from '../../src/ui/Screen'

const ORG_LINE = '#c3cde1'
/** Index-only root id when the endpoint returns a forest; it is never rendered. */
const COMPANY_ID = '__company__'
/**
 * Счётчики «N чел.» / «N подчинённых» / «N всего в структуре» скрыты по просьбе заказчика
 * (16.09.2026): цифры расходятся с ЗУП, пока бэк не починит подсчёт. Вернуть — переключить флаг.
 */
const SHOW_COUNTS = false

/** Пока счётчики скрыты — пилюля без числа, чтобы было видно, у кого есть подчинённые. */
const HAS_REPORTS: Record<Lang, string> = {
  ru: 'Есть подчинённые',
  en: 'Has reports',
  az: 'Tabeçiləri var',
  ka: 'ჰყავს ქვემდებარეები',
}

function orgPlural(n: number, lang: Lang): string {
  if (!SHOW_COUNTS) return HAS_REPORTS[lang] ?? HAS_REPORTS.en
  if (lang === 'az') return `${n} tabeçi`
  if (lang === 'ka') return `${n} ქვემდებარე`
  if (lang !== 'ru') return `${n} ${n === 1 ? 'report' : 'reports'}`
  return `${n} ${pluralRu(n, 'подчинённый', 'подчинённых', 'подчинённых')}`
}

const isCompany = (n: PositionNode) => n.kind === 'company'

/** Люди в поддереве: без вакансий, человек на двух должностях — один раз (численность компании). */
function peopleIn(n: PositionNode): number {
  const ids = new Set<string>()
  const walk = (x: PositionNode) => {
    if (x.holder) ids.add(x.holder.id)
    x.reports?.forEach(walk)
  }
  n.reports?.forEach(walk)
  return ids.size
}

/** Акцентный цвет узла: компания — navy, держатель — по имени (стабильный), вакансия — нейтральный. */
const nodeColor = (n: PositionNode) =>
  isCompany(n) ? palette.coverNavy : n.holder ? avColor(n.holder.fullName) : '#8b97a8'

// ── Org index (flat maps over the live tree) ────────────────────────────────────
interface OrgIndex {
  rootId: string
  byId: Map<string, PositionNode>
  parentOf: Map<string, string>
  depthOf: Map<string, number>
  /** Первый узел держателя — для старых ссылок /org?focus=<userId>. */
  byHolder: Map<string, string>
  /** Full subtree headcount (excluding the node itself). */
  totalOf: Map<string, number>
}

function buildIndex(forest: PositionNode[]): OrgIndex {
  const root: PositionNode =
    forest.length === 1
      ? forest[0]
      : { id: COMPANY_ID, title: '', holder: null, reports: forest }
  const byId = new Map<string, PositionNode>()
  const parentOf = new Map<string, string>()
  const depthOf = new Map<string, number>()
  const byHolder = new Map<string, string>()
  const totalOf = new Map<string, number>()
  const walk = (n: PositionNode, depth: number): number => {
    byId.set(n.id, n)
    depthOf.set(n.id, depth)
    if (n.holder && !byHolder.has(n.holder.id)) byHolder.set(n.holder.id, n.id)
    let total = 0
    for (const c of n.reports ?? []) {
      parentOf.set(c.id, n.id)
      total += 1 + walk(c, depth + 1)
    }
    totalOf.set(n.id, total)
    return total
  }
  walk(root, 0)
  return { rootId: root.id, byId, parentOf, depthOf, byHolder, totalOf }
}

// ── Motion helpers ──────────────────────────────────────────────────────────────
/** «Дышащее» кольцо вокруг аватара в фокусе (пауза при reduce-motion). */
function PulseRing({ size, color }: { size: number; color: string }) {
  const [on, setOn] = useState(false)
  useEffect(() => {
    if (prefersReducedMotion()) return
    const iv = setInterval(() => setOn((v) => !v), 1500)
    return () => clearInterval(iv)
  }, [])
  return (
    <Stack
      position="absolute"
      top={-7}
      left={-7}
      width={size + 14}
      height={size + 14}
      borderRadius={999}
      borderWidth={2}
      borderColor={color}
      pointerEvents="none"
      animation="lazy"
      opacity={on ? 0.08 : 0.5}
      scale={on ? 1.22 : 1}
    />
  )
}

/** Вертикальный коннектор, «вырастающий» с задержкой. */
function GrowLine({ delay = 0, height = 26 }: { delay?: number; height?: number }) {
  const [on, setOn] = useState(() => prefersReducedMotion())
  useEffect(() => {
    if (prefersReducedMotion()) return
    const t = setTimeout(() => setOn(true), delay)
    return () => clearTimeout(t)
  }, [delay])
  return (
    <Stack
      width={2}
      borderRadius={1}
      backgroundColor={ORG_LINE}
      animation="quicker"
      height={on ? height : 0}
      opacity={on ? 1 : 0}
    />
  )
}

// ── Small bits ──────────────────────────────────────────────────────────────────
/** Props for the name hit-zone: tinted chip on hover, squeeze on press. */
const personHitProps = (accent: string) => ({
  cursor: 'pointer' as const,
  animation: 'quicker' as const,
  borderRadius: 7,
  // breathing room for the tint without shifting layout: padding compensated by negative margin
  paddingHorizontal: 6,
  paddingVertical: 3,
  marginHorizontal: -6,
  marginVertical: -3,
  hoverStyle: { backgroundColor: `${accent}14` },
  pressStyle: { backgroundColor: `${accent}1F`, scale: 0.97 },
})

function Pill({ icon, bg, color, children }: { icon?: string; bg: string; color: string; children: ReactNode }) {
  return (
    <XStack alignItems="center" gap={5} backgroundColor={bg} borderRadius={999} paddingHorizontal={10} paddingVertical={4}>
      {icon && <Icon name={icon} size={11} color={color} />}
      <Text fontSize={11.5} fontWeight="600" color={color}>
        {children}
      </Text>
    </XStack>
  )
}

/** Аватар узла: компания — здание, держатель — обычный, вакансия — пунктирный круг с силуэтом. */
function NodeAvatar({ node, size }: { node: PositionNode; size: number }) {
  if (isCompany(node))
    return (
      <Stack
        width={size}
        height={size}
        borderRadius={999}
        alignItems="center"
        justifyContent="center"
        backgroundColor={`${palette.coverNavy}1A`}
      >
        <Icon name="building-2" size={size * 0.5} color={palette.coverNavy} />
      </Stack>
    )
  if (node.holder)
    return (
      <Avatar
        name={node.holder.fullName}
        size={size}
        color={avColor(node.holder.fullName)}
        uri={node.holder.avatarUrl}
        status={node.holder.presence}
      />
    )
  return (
    <Stack
      width={size}
      height={size}
      borderRadius={999}
      borderWidth={2}
      borderColor="$borderSecondary"
      borderStyle="dashed"
      alignItems="center"
      justifyContent="center"
      backgroundColor="$bgLayout"
    >
      <Icon name="user" size={size * 0.44} color="$tertiary" />
    </Stack>
  )
}

// ── Cards ───────────────────────────────────────────────────────────────────────
/** Вышестоящая должность — компактная строка над фокусом, клик поднимает фокус. */
function ManagerCard({ node, vacancyLabel, onPress, onPerson }: {
  node: PositionNode
  vacancyLabel: string
  onPress: () => void
  onPerson: (n: PositionNode) => void
}) {
  const { td } = useApp()
  const accent = nodeColor(node)
  return (
    <XStack
      onPress={onPress}
      cursor="pointer"
      animation="quicker"
      alignItems="center"
      gap={10}
      paddingVertical={9}
      paddingHorizontal={14}
      borderRadius={12}
      backgroundColor="$background"
      borderWidth={1}
      borderColor="$borderSecondary"
      shadowColor={palette.shadow}
      shadowOpacity={0.08}
      shadowRadius={8}
      shadowOffset={{ width: 0, height: 2 }}
      hoverStyle={{ y: -3, scale: 1.02, borderColor: accent, shadowOpacity: 0.22, shadowRadius: 14 }}
      pressStyle={{ scale: 0.97 }}
    >
      <XStack alignItems="center" gap={10}>
        <NodeAvatar node={node} size={28} />
        <YStack flexShrink={1}>
          <Text fontSize={13} fontWeight="700" color="$color" numberOfLines={1}>
            {td(node.title)}
          </Text>
          {/* имя держателя → карточка сотрудника (не переводит фокус); у компании подписи нет */}
          {!isCompany(node) && (
            <Text
              fontSize={11}
              color={node.holder ? '$muted' : '$tertiary'}
              numberOfLines={1}
              alignSelf="flex-start"
              onPress={
                node.holder
                  ? (e) => {
                      e.stopPropagation()
                      onPerson(node)
                    }
                  : undefined
              }
              {...(node.holder ? personHitProps(accent) : null)}
              {...(node.holder ? { accessibilityRole: 'button' as const, accessibilityLabel: node.holder.fullName } : null)}
            >
              {node.holder?.fullName ?? vacancyLabel}
            </Text>
          )}
        </YStack>
      </XStack>
      <Stack rotate="180deg" marginLeft={2}>
        <Icon name="chevron-down" size={14} color="$tertiary" />
      </Stack>
    </XStack>
  )
}

/** Должность (или компания) в сетке, клик переводит в фокус. */
function PositionCard({ node, width, lang, vacancyLabel, onPress, onPerson }: {
  node: PositionNode
  width: number
  lang: Lang
  vacancyLabel: string
  onPress: () => void
  onPerson: (n: PositionNode) => void
}) {
  const { l } = useApp()
  const accent = nodeColor(node)
  const company = isCompany(node)
  const vacant = !node.holder && !company
  const direct = node.reports?.length ?? 0
  return (
    <YStack
      onPress={onPress}
      cursor="pointer"
      animation="quicker"
      width={width}
      alignItems="center"
      gap={7}
      paddingTop={18}
      paddingBottom={14}
      paddingHorizontal={12}
      borderRadius={14}
      backgroundColor="$background"
      borderWidth={1}
      borderColor="$borderSecondary"
      borderStyle={vacant ? 'dashed' : 'solid'}
      opacity={vacant ? 0.85 : 1}
      overflow="hidden"
      shadowColor={palette.shadow}
      shadowOpacity={vacant ? 0.03 : 0.08}
      shadowRadius={8}
      shadowOffset={{ width: 0, height: 2 }}
      hoverStyle={{ y: -5, scale: 1.03, borderColor: accent, shadowOpacity: 0.25, shadowRadius: 18 }}
      pressStyle={{ scale: 0.96 }}
    >
      {!vacant && <Stack position="absolute" top={0} left={0} right={0} height={3} backgroundColor={accent} />}
      <YStack alignItems="center" gap={7}>
        <NodeAvatar node={node} size={46} />
        <YStack alignItems="center" gap={2}>
          <Text fontSize={13} fontWeight="700" color="$color" textAlign="center" numberOfLines={2}>
            {tr(node.title, lang)}
          </Text>
          {/* имя держателя → карточка сотрудника (не переводит фокус); у компании подписи нет */}
          {!company && (
            <Text
              fontSize={11.5}
              color={vacant ? '$tertiary' : '$muted'}
              fontStyle={vacant ? 'italic' : 'normal'}
              textAlign="center"
              numberOfLines={2}
              onPress={
                vacant
                  ? undefined
                  : (e) => {
                      e.stopPropagation()
                      onPerson(node)
                    }
              }
              {...(vacant ? null : personHitProps(accent))}
              {...(vacant ? null : { accessibilityRole: 'button' as const, accessibilityLabel: node.holder!.fullName })}
            >
              {node.holder?.fullName ?? vacancyLabel}
            </Text>
          )}
        </YStack>
      </YStack>
      {SHOW_COUNTS && company ? (
        <Pill icon="users" bg={`${accent}1A`} color={accent}>
          {`${peopleIn(node)} ${l('org.people')}`}
        </Pill>
      ) : !company && direct > 0 ? (
        <Pill icon="users" bg={`${accent}1A`} color={accent}>
          {orgPlural(direct, lang)}
        </Pill>
      ) : (
        <Stack height={22} />
      )}
    </YStack>
  )
}

/** Должность в фокусе — hero-карточка в центре сцены. */
function FocusCard({ node, total, totalLabel, vacancyLabel, lang, onPerson }: {
  node: PositionNode
  total: number
  totalLabel: string
  vacancyLabel: string
  lang: Lang
  onPerson: (n: PositionNode) => void
}) {
  const { l } = useApp()
  const accent = nodeColor(node)
  const company = isCompany(node)
  const direct = node.reports?.length ?? 0
  return (
    <YStack
      animation="bouncy"
      enterStyle={{ opacity: 0, scale: 0.86 }}
      opacity={1}
      scale={1}
      width={360}
      maxWidth="100%"
      alignItems="center"
      gap={9}
      paddingTop={26}
      paddingBottom={22}
      paddingHorizontal={20}
      borderRadius={18}
      backgroundColor="$background"
      borderWidth={1}
      borderColor="$borderSecondary"
      overflow="hidden"
      shadowColor="rgba(20,40,100,0.35)"
      shadowOpacity={1}
      shadowRadius={22}
      shadowOffset={{ width: 0, height: 8 }}
    >
      {/* accent bar + decorative blooms */}
      <Stack position="absolute" top={0} left={0} right={0} height={4} backgroundColor={accent} />
      <Stack position="absolute" top={-46} right={-46} width={140} height={140} borderRadius={999} backgroundColor={accent} opacity={0.07} pointerEvents="none" />
      <Stack position="absolute" bottom={-38} left={-38} width={110} height={110} borderRadius={999} backgroundColor={accent} opacity={0.05} pointerEvents="none" />

      <YStack alignItems="center" gap={9}>
        <Stack>
          <PulseRing size={72} color={accent} />
          <NodeAvatar node={node} size={72} />
        </Stack>
        <YStack alignItems="center" gap={3}>
          <Text fontSize={19} fontWeight="800" color="$color" textAlign="center" letterSpacing={-0.3}>
            {tr(node.title, lang)}
          </Text>
          {/* имя держателя → карточка сотрудника; у компании подписи нет */}
          {!company && (
            <Text
              fontSize={13.5}
              color={node.holder ? '$muted' : '$tertiary'}
              fontStyle={node.holder ? 'normal' : 'italic'}
              textAlign="center"
              onPress={node.holder ? () => onPerson(node) : undefined}
              {...(node.holder ? personHitProps(accent) : null)}
              {...(node.holder ? { accessibilityRole: 'button' as const, accessibilityLabel: node.holder.fullName } : null)}
            >
              {node.holder?.fullName ?? vacancyLabel}
            </Text>
          )}
        </YStack>
      </YStack>
      <XStack flexWrap="wrap" justifyContent="center" gap={7} marginTop={3}>
        {SHOW_COUNTS && company && (
          <Pill icon="users" bg={`${accent}1A`} color={accent}>
            {`${peopleIn(node)} ${l('org.people')}`}
          </Pill>
        )}
        {!company && direct > 0 && (
          <Pill icon="users" bg={`${accent}1A`} color={accent}>
            {orgPlural(direct, lang)}
          </Pill>
        )}
        {SHOW_COUNTS && !company && total > direct && (
          <Pill icon="network" bg="$bgLayout" color="$muted">
            {`${total} ${totalLabel}`}
          </Pill>
        )}
      </XStack>
    </YStack>
  )
}

// ── Breadcrumbs (путь от корня до фокуса, каждый уровень кликабелен) ────────────
function Crumbs({ path, current, onGo }: { path: PositionNode[]; current: PositionNode; onGo: (id: string) => void }) {
  if (path.length === 0) return null
  return (
    <XStack flexWrap="wrap" alignItems="center" justifyContent="center" gap={4} marginBottom={20}>
      {path.map((n, i) => (
        <XStack key={n.id} alignItems="center" gap={4}>
          <Enter index={i} step={35} from={6}>
            <XStack
              onPress={() => onGo(n.id)}
              cursor="pointer"
              animation="quicker"
              alignItems="center"
              gap={6}
              paddingHorizontal={9}
              paddingVertical={5}
              borderRadius={999}
              hoverStyle={{ backgroundColor: '$bgLayout', scale: 1.04 }}
              pressStyle={{ scale: 0.95 }}
            >
              <NodeAvatar node={n} size={18} />
              <Text fontSize={12} fontWeight="600" color="$muted" numberOfLines={1}>
                {n.title}
              </Text>
            </XStack>
          </Enter>
          <Icon name="chevron-right" size={12} color="$tertiary" />
        </XStack>
      ))}
      <Enter index={path.length} step={35} from={6}>
        <XStack alignItems="center" gap={6} paddingHorizontal={9} paddingVertical={5}>
          <Text fontSize={12} fontWeight="800" color="$color" numberOfLines={1}>
            {current.title}
          </Text>
        </XStack>
      </Enter>
    </XStack>
  )
}

// ── Tree (focus) view ───────────────────────────────────────────────────────────
function TreeView({ onPerson }: { onPerson: (n: PositionNode) => void }) {
  const { l, lang } = useApp()
  const { isPhone } = useResponsive()
  const router = useRouter()
  const params = useLocalSearchParams<{ focus?: string }>()
  const orgQuery = useOrgChartHolding()
  const { data: liveOrg } = orgQuery

  const idx = useMemo(
    () => (liveOrg && liveOrg.length > 0 ? buildIndex(liveOrg) : null),
    [liveOrg],
  )

  const rawFocus = typeof params.focus === 'string' ? params.focus : undefined
  // Фокус: id узла; старые ссылки с userId держателя работают через byHolder.
  const focusId =
    idx && rawFocus
      ? idx.byId.has(rawFocus)
        ? rawFocus
        : (idx.byHolder.get(rawFocus) ?? idx.rootId)
      : idx?.rootId

  // Направление перехода: вглубь (вниз) / наверх — сцена въезжает с нужной стороны.
  const prevRef = useRef<string | undefined>(focusId)
  // Ширина ряда дочерних юрлиц: горизонтальная шина рисуется, только если все карточки влезли в строку.
  const [rowW, setRowW] = useState(0)
  const dir =
    !idx || !focusId || prevRef.current === focusId || prevRef.current === undefined
      ? 0
      : (idx.depthOf.get(focusId) ?? 0) >= (idx.depthOf.get(prevRef.current) ?? 0)
        ? 1
        : -1
  useEffect(() => {
    prevRef.current = focusId
  }, [focusId])

  if (!idx || !focusId) {
    // isError даёт статус-осознанный текст (403/прочее) + «Повторить»; honestly-empty
    // чарт (data === []) остаётся при существующем «недоступна» — QueryState его не трогает.
    return (
      <QueryState q={orgQuery}>
        <YStack backgroundColor="$background" borderWidth={1} borderColor="$borderSecondary" borderRadius={7} padding={48} alignItems="center">
          <Text fontSize={13} color="$muted">
            {l('org.treeEmpty')}
          </Text>
        </YStack>
      </QueryState>
    )
  }

  const node = idx.byId.get(focusId)!
  const managerId = idx.parentOf.get(focusId)
  const manager = managerId && managerId !== COMPANY_ID ? idx.byId.get(managerId) : undefined
  const kids = node.reports ?? []
  const total = idx.totalOf.get(focusId) ?? 0
  const isCompanyRoot = node.id === COMPANY_ID

  // Путь от корня до родителя фокуса (для хлебных крошек).
  const path: PositionNode[] = []
  for (let p = managerId; p; p = idx.parentOf.get(p)) path.unshift(idx.byId.get(p)!)
  const visiblePath = path.filter((p) => p.id !== COMPANY_ID)

  // Фокус живёт в URL: /org?focus=<nodeId> — ссылку можно шарить. Возврат на корень
  // чистит query целиком (replace, чтобы не плодить историю мусорными шагами).
  const setFocus = (id: string) => {
    if (id === focusId) return
    if (id === idx.rootId) router.replace('/org')
    else router.setParams({ focus: id })
  }

  const cardW = isPhone ? 156 : 192
  // Корень холдинга: корп. центр во главе, дочерние юрлица под ним, соединены линиями.
  const holding = isCompanyRoot ? kids.find((k) => k.isHolding) : undefined
  const subsidiaries = holding ? kids.filter((k) => k !== holding) : []
  const busW =
    subsidiaries.length * cardW + (subsidiaries.length - 1) * 12 <= rowW ? (subsidiaries.length - 1) * (cardW + 12) : 0

  return (
    <YStack
      backgroundColor="$background"
      borderWidth={1}
      borderColor="$borderSecondary"
      borderRadius={16}
      paddingVertical={28}
      paddingHorizontal={isPhone ? 14 : 24}
      minHeight={420}
    >
      <FadeIn>
        <Text fontSize={12.5} color="$muted" textAlign="center" marginBottom={16}>
          {l('org.focusHint')}
        </Text>
      </FadeIn>

      {idx.rootId === COMPANY_ID && focusId !== COMPANY_ID && (
        <Enter index={0} from={-6}>
          <XStack
            onPress={() => setFocus(idx.rootId)}
            accessibilityRole="button"
            cursor="pointer"
            animation="quicker"
            alignSelf="center"
            alignItems="center"
            gap={6}
            marginBottom={16}
            paddingHorizontal={11}
            paddingVertical={7}
            borderRadius={999}
            backgroundColor="$bgLayout"
            hoverStyle={{ scale: 1.03 }}
            pressStyle={{ scale: 0.97 }}
          >
            <Icon name="arrow-left" size={14} color="$muted" />
            <Text fontSize={12} fontWeight="700" color="$muted">
              {l('org.allCompanies')}
            </Text>
          </XStack>
        </Enter>
      )}

      {!isCompanyRoot && <Crumbs path={visiblePath} current={node} onGo={setFocus} />}

      {/* Сцена перезаезжает целиком при смене фокуса (keyed remount + направление). */}
      <YStack
        key={focusId}
        alignItems="center"
        width="100%"
        animation="quicker"
        enterStyle={{ opacity: 0, y: dir * 30, scale: 0.99 }}
        opacity={1}
        y={0}
        scale={1}
      >
        {manager && (
          <>
            <Enter index={0} from={-10}>
              <ManagerCard node={manager} vacancyLabel={l('org.vacancy')} onPress={() => setFocus(manager.id)} onPerson={onPerson} />
            </Enter>
            <GrowLine delay={100} height={26} />
          </>
        )}

        {!isCompanyRoot && (
          <FocusCard
            node={node}
            total={total}
            totalLabel={l('org.total')}
            vacancyLabel={l('org.vacancy')}
            lang={lang}
            onPerson={onPerson}
          />
        )}

        {holding ? (
          <>
            <Enter index={2} step={40} from={16}>
              <YStack testID="org-holding">
                <PositionCard
                  node={holding}
                  width={cardW}
                  lang={lang}
                  vacancyLabel={l('org.vacancy')}
                  onPress={() => setFocus(holding.id)}
                  onPerson={onPerson}
                />
              </YStack>
            </Enter>
            {subsidiaries.length > 0 && (
              <>
                <GrowLine delay={220} height={22} />
                {busW > 0 && <Stack width={busW} height={2} borderRadius={1} backgroundColor={ORG_LINE} />}
                <XStack
                  testID="org-subsidiaries"
                  width="100%"
                  flexWrap="wrap"
                  justifyContent="center"
                  gap={12}
                  onLayout={(e) => setRowW(e.nativeEvent.layout.width)}
                >
                  {subsidiaries.map((k, i) => (
                    <Enter key={k.id} index={i + 3} step={40} from={16}>
                      <YStack alignItems="center">
                        <Stack width={2} height={18} backgroundColor={ORG_LINE} />
                        <PositionCard
                          node={k}
                          width={cardW}
                          lang={lang}
                          vacancyLabel={l('org.vacancy')}
                          onPress={() => setFocus(k.id)}
                          onPerson={onPerson}
                        />
                      </YStack>
                    </Enter>
                  ))}
                </XStack>
              </>
            )}
          </>
        ) : kids.length > 0 ? (
          <>
            {!isCompanyRoot && <GrowLine delay={220} height={30} />}
            <Enter index={2} step={40}>
              <XStack alignItems="center" gap={7} marginBottom={14}>
                <Icon name="users" size={13} color="$muted" />
                <Text fontSize={12} fontWeight="700" color="$muted" letterSpacing={0.4} textTransform="uppercase">
                  {l(isCompanyRoot ? 'org.companies' : 'org.team')}
                </Text>
                <XStack backgroundColor="$bgLayout" borderRadius={999} paddingHorizontal={8} paddingVertical={2}>
                  <Text fontSize={11} fontWeight="700" color="$muted">
                    {kids.length}
                  </Text>
                </XStack>
              </XStack>
            </Enter>
            <XStack flexWrap="wrap" justifyContent="center" gap={12} maxWidth={5 * (cardW + 12)}>
              {kids.map((k, i) => (
                <Enter key={k.id} index={i + 3} step={40} from={16}>
                  <PositionCard
                    node={k}
                    width={cardW}
                    lang={lang}
                    vacancyLabel={l('org.vacancy')}
                    onPress={() => setFocus(k.id)}
                    onPerson={onPerson}
                  />
                </Enter>
              ))}
            </XStack>
          </>
        ) : (
          <FadeIn marginTop={22}>
            <XStack alignItems="center" gap={7} backgroundColor="$bgLayout" borderRadius={999} paddingHorizontal={14} paddingVertical={7}>
              <Icon name="circle-dot" size={12} color="$tertiary" />
              <Text fontSize={12} color="$muted">
                {l('org.noReports')}
              </Text>
            </XStack>
          </FadeIn>
        )}
      </YStack>
    </YStack>
  )
}

// ── Departments view ───────────────────────────────────────────────────────────
// Юрлица → подразделения 1С (GET /users/departments/tree, макет 29.10.2026): карточка юрлица,
// в ней строки подразделений с руководителем; «+» раскрывает людей подразделения, «−» сворачивает.
// Руководитель — по признаку руководящей позиции в 1С, не угадывается. Численность людей не
// выводим (SHOW_COUNTS) — в шапке только число подразделений.
function DeptRow({ unit, first, open, onToggle, onPerson }: {
  unit: DepartmentTreeUnit
  first: boolean
  open: boolean
  onToggle: () => void
  onPerson: (userId: string) => void
}) {
  const { l } = useApp()
  return (
    <YStack {...(first ? null : { borderTopWidth: 1, borderColor: '$borderSecondary' })}>
      <XStack
        {...pressable}
        onPress={onToggle}
        aria-expanded={open}
        aria-label={`${unit.name}: ${l(open ? 'org.hidePeople' : 'org.showPeople')}`}
        cursor="pointer"
        alignItems="center"
        gap={12}
        paddingHorizontal={16}
        paddingVertical={12}
        hoverStyle={{ backgroundColor: '$backgroundHover' }}
      >
        <YStack flex={1} minWidth={0} gap={3}>
          <Text fontSize={13} fontWeight="600" color="$color">
            {unit.name}
          </Text>
          <XStack alignItems="center" gap={5} minWidth={0}>
            <Icon name="user" size={11} color="$tertiary" />
            <Text fontSize={11} color="$muted" numberOfLines={1} flexShrink={1}>
              {unit.head?.fullName ?? l('org.noHead')}
            </Text>
          </XStack>
        </YStack>
        <PlusMinus open={open} />
      </XStack>

      <Collapse open={open}>
        <YStack backgroundColor="$bgLayout" paddingHorizontal={16} paddingVertical={8}>
          {unit.members.map((m) => (
            <XStack
              key={m.id}
              {...pressable}
              onPress={() => onPerson(m.id)}
              cursor="pointer"
              alignItems="center"
              gap={10}
              paddingVertical={6}
              hoverStyle={{ opacity: 0.8 }}
            >
              <Avatar name={m.fullName} seed={m.id} size="sm" uri={api.assetUrl(m.avatarUrl)} />
              <YStack flex={1} minWidth={0}>
                <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
                  {m.fullName}
                </Text>
                {m.jobTitle ? (
                  <Text fontSize={11} color="$muted" numberOfLines={1}>
                    {m.jobTitle}
                  </Text>
                ) : null}
              </YStack>
              {m.isHead ? <Tag tone="blue">{l('org.head')}</Tag> : null}
            </XStack>
          ))}
        </YStack>
      </Collapse>
    </YStack>
  )
}

/**
 * «+» ↔ «−» одной фигурой: две полоски, вертикальная плавно ложится на горизонтальную
 * (поворот 90°) и обратно; рамка и фон кнопки перетекают в акцент. Reduce-motion — без перехода.
 */
function PlusMinus({ open }: { open: boolean }) {
  const anim = prefersReducedMotion() ? undefined : 'quicker'
  const color = open ? '$primary' : '$color'
  return (
    <Stack
      width={28}
      height={28}
      flexShrink={0}
      borderRadius={7}
      borderWidth={1}
      animation={anim}
      borderColor={open ? '$primary' : '$borderColor'}
      backgroundColor={open ? '$primaryBg' : '$background'}
      alignItems="center"
      justifyContent="center"
    >
      <Stack position="absolute" width={12} height={2} borderRadius={1} backgroundColor={color} animation={anim} />
      <Stack
        position="absolute"
        width={2}
        height={12}
        borderRadius={1}
        backgroundColor={color}
        animation={anim}
        rotate={open ? '90deg' : '0deg'}
      />
    </Stack>
  )
}

/**
 * Плавное раскрытие: внешний блок анимирует высоту 0 ↔ высота содержимого (меряется у
 * вложенного абсолютного слоя — ему внешняя высота не мешает) и прозрачность. Содержимое
 * монтируется при первом раскрытии и дальше живёт: закрытие тоже анимируется, а свёрнутые
 * подразделения, которые ни разу не открывали, не грузят аватары зря.
 */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  const [h, setH] = useState(0)
  const [mounted, setMounted] = useState(open)
  useEffect(() => {
    if (open) setMounted(true)
  }, [open])
  if (!mounted) return null
  return (
    <Stack
      overflow="hidden"
      animation={prefersReducedMotion() ? undefined : 'quicker'}
      height={open ? h : 0}
      opacity={open ? 1 : 0}
      aria-hidden={!open}
    >
      <Stack
        position="absolute"
        top={0}
        left={0}
        right={0}
        onLayout={(e) => {
          const v = Math.round(e.nativeEvent.layout.height)
          setH((prev) => (prev === v ? prev : v))
        }}
      >
        {children}
      </Stack>
    </Stack>
  )
}

function DeptsView({ onPerson }: { onPerson: (userId: string) => void }) {
  const { l } = useApp()
  const treeQuery = useDepartmentTree()
  const companies = treeQuery.data ?? []
  // Раскрытые подразделения: ключ «юрлицо:подразделение» — несколько сразу.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set())
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })
  // Колонки — по ширине самого контейнера (как сетка «Календаря»): окно не видит сайдбар.
  const [w, setW] = useState<number | null>(null)
  const cols = w == null ? 3 : w >= 1400 ? 4 : w >= 1000 ? 3 : w >= 640 ? 2 : 1

  if (companies.length === 0) {
    return (
      <QueryState q={treeQuery}>
        <YStack
          backgroundColor="$background"
          borderWidth={1}
          borderColor="$borderSecondary"
          borderRadius={7}
          padding={48}
          alignItems="center"
        >
          <Text fontSize={13} color="$muted">
            {treeQuery.isLoading ? '…' : l('org.deptsEmpty')}
          </Text>
        </YStack>
      </QueryState>
    )
  }
  return (
    <XStack
      flexWrap="wrap"
      margin={-8}
      alignItems="flex-start"
      onLayout={(e) => {
        const v = Math.round(e.nativeEvent.layout.width)
        setW((prev) => (prev === v ? prev : v))
      }}
    >
      {companies.map((c, i) => (
        <Enter key={c.id} index={Math.min(i, 8)} width={`${100 / cols}%`} padding={8}>
          <SectionCard
            title={
              <Text fontSize={14} fontWeight="700" color="$primary" flexShrink={1}>
                {c.name}
              </Text>
            }
            extra={
              <Text fontSize={12} color="$muted" flexShrink={0}>
                {c.departments.length} {l('org.deptShort')}
              </Text>
            }
            pad={0}
          >
            {c.departments.map((d, j) => {
              const key = `${c.id}:${d.id}`
              return <DeptRow key={key} unit={d} first={j === 0} open={open.has(key)} onToggle={() => toggle(key)} onPerson={onPerson} />
            })}
          </SectionCard>
        </Enter>
      ))}
    </XStack>
  )
}

// ── Header segment control ──────────────────────────────────────────────────────
function Seg({ active, label, icon, onPress }: { active: boolean; label: string; icon: string; onPress: () => void }) {
  return (
    <XStack
      onPress={onPress}
      cursor="pointer"
      animation="quick"
      alignItems="center"
      gap={6}
      paddingHorizontal={12}
      paddingVertical={7}
      borderRadius={5}
      backgroundColor={active ? '$background' : 'transparent'}
      {...(active ? { shadowColor: palette.shadow, shadowOpacity: 0.12, shadowRadius: 6, shadowOffset: { width: 0, height: 1 } } : null)}
    >
      <Icon name={icon} size={15} color={active ? '$primary' : '$muted'} />
      <Text fontSize={13} fontWeight="600" color={active ? '$color' : '$muted'}>
        {label}
      </Text>
    </XStack>
  )
}

// ── Screen ───────────────────────────────────────────────────────────────────────
export default function OrgScreen() {
  const { l } = useApp()
  const qc = useQueryClient()
  const [view, setView] = useState<'tree' | 'depts'>('tree')
  const [sel, setSel] = useState<PersonVM | null>(null)
  // Узел → карточка ДЕРЖАТЕЛЯ: полный профиль тянем отдельно (в узле только имя/аватар).
  // Вакансия и синтетический корень карточки не имеют.
  const openUser = (userId: string) => {
    qc.fetchQuery({ queryKey: ['user', userId], queryFn: () => api.user(userId) })
      .then((profile) => setSel(liveVM(profile)))
      .catch(() => undefined)
  }
  const onPerson = (n: PositionNode) => {
    if (n.holder) openUser(n.holder.id)
  }

  return (
    <>
      <Page
        title={l('org.title')}
        subtitle={l('org.subtitle')}
        action={
          <XStack gap={4} backgroundColor="$bgLayout" padding={4} borderRadius={7} borderWidth={1} borderColor="$borderSecondary">
            <Seg active={view === 'tree'} label={l('org.tree')} icon="git-fork" onPress={() => setView('tree')} />
            <Seg active={view === 'depts'} label={l('org.depts')} icon="layout-grid" onPress={() => setView('depts')} />
          </XStack>
        }
      >
        {view === 'depts' ? <DeptsView onPerson={openUser} /> : <TreeView onPerson={onPerson} />}
      </Page>

      <PersonDrawer p={sel} onClose={() => setSel(null)} />
    </>
  )
}
